import React, { useEffect, useState, createElement } from 'react';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { C0, E0, EQ0, M0, ROLES0, S0, TIPOS, U0, moduleCards } from './data';
import { useStoredState } from './storage';
import { useSucursales, useUsuarios, useRoles, useConsumos, useAuditoria, useConfiguracion, guardarUsuario } from './db';
import type { AppState, Consumo, Empleado, Equipo, Medicion, Perfil, RolPermiso, Sucursal, Usuario, View } from './types';
import { Field, Modal, T, glass, inputStyle, primaryButton, ghostButton, useIsMobile } from './ui';
import { Login } from './modules/Login';
import { ConsumoEnergetico } from './modules/ConsumoEnergetico';
import { Alertas } from './modules/Alertas';
import { Auditoria } from './modules/Auditoria';
import { Configuracion } from './modules/Configuracion';
import { Dashboard } from './modules/Dashboard';
import { Reportes } from './modules/Reportes';
import { RolesPermisos } from './modules/RolesPermisos';
import { Sucursales } from './modules/Sucursales';
import { Usuarios } from './modules/Usuarios';
import { ConexionSupabase } from './modules/ConexionSupabase';
import GasApp from './modules/gas';
const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 5);
const stamp = () => new Date().toLocaleString('es-DO', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit'
});
const xml = (v: string | number) => String(v).replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;');
function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
type ModalKind = 'sucursal' | 'empleado' | 'medicion' | 'equipo' | 'rol' | 'usuario' | null;
type PermFlags = {
  leer: boolean;
  editar: boolean;
  archivar: boolean;
};
// Módulos actuales del sistema (los demás fueron retirados de la app).
const MODULE_KEYS: string[] = ['consumo', 'costoConsumo', 'sucursales', 'empleados', 'mediciones', 'equipos', 'historial', 'mapa', 'reportes', 'alertas', 'usuarios', 'roles', 'gas', 'auditoria', 'configuracion'];
const MODULE_LABELS: Record<string, string> = {
  consumo: 'Consumo',
  costoConsumo: 'Ver costo estimado',
  sucursales: 'Sucursales',
  reportes: 'Reportes',
  alertas: 'Alertas',
  usuarios: 'Usuarios',
  roles: 'Roles y permisos',
  gas: 'Gas Industrial',
  auditoria: 'Auditoría',
  configuracion: 'Configuración'
};
const emptyPermisos = (): Record<string, PermFlags> => Object.fromEntries(MODULE_KEYS.map(k => [k, {
  leer: false,
  editar: false,
  archivar: false
}]));
export default function App() {
  const isMobile = useIsMobile();
  // Entidades sincronizadas con tablas reales de Supabase (caché local de respaldo)
  const [sucs, setSucs] = useSucursales(S0);
  const [usuarios, setUsuarios] = useUsuarios(U0);
  const [roles, setRoles] = useRoles(ROLES0);
  const [consumos, setConsumos] = useConsumos();
  const {
    audit,
    log
  } = useAuditoria(stamp);
  const [session, setSession] = useStoredState<Usuario | null>('getr.session', null);
  // El usuario logueado es exactamente lo que devolvió la Edge Function
  // 'login' (sin password). Ya no se busca en el array local `usuarios`:
  // ese array puede no incluir a este usuario si está filtrado/paginado,
  // y además así evitamos depender de tener la lista completa cargada.
  const sessionUser = session;
  const perfil: Perfil = {
    nombre: sessionUser?.nombre ?? 'Usuario',
    correo: sessionUser?.correo ?? '',
    rol: sessionUser?.rol ?? 'tecnico'
  };
  const {
    notif,
    prefs,
    setNotif,
    setPrefs
  } = useConfiguracion({
    perfil,
    notif: {
      fueraRango: true,
      resumen: true,
      auditoria: false,
      email: true
    },
    prefs: {
      luxMin: 250,
      idioma: 'Español',
      zona: 'America/Santo_Domingo',
      tema: 'Oscuro'
    }
  }, session?.id ?? 'guest');
  // Al editar el perfil (nombre/correo) se actualiza vía la Edge Function
  // 'guardar-usuario' (no hay escritura directa a la tabla `usuarios`) y se
  // refleja también en la sesión local para que la UI quede sincronizada.
  const setPerfil = async (value: Perfil) => {
    if (!session) return;
    const correoNuevo = value.correo.trim().toLowerCase();
    const enUsoPorOtro = usuarios.some(u => u.id !== session.id && u.correo.trim().toLowerCase() === correoNuevo);
    if (correoNuevo && enUsoPorOtro) {
      alert('Ese correo ya está en uso por otro usuario.');
      return;
    }
    const {
      ok,
      error
    } = await guardarUsuario({
      id: session.id,
      nombre: value.nombre,
      correo: value.correo,
      rol: session.rol,
      sucursalesIds: session.sucursalesIds,
      archivado: session.archivado
    });
    if (!ok) {
      alert(error ?? 'No se pudo actualizar el perfil.');
      return;
    }
    setSession({
      ...session,
      nombre: value.nombre,
      correo: value.correo
    });
    setUsuarios(prev => prev.map(u => u.id === session.id ? {
      ...u,
      nombre: value.nombre,
      correo: value.correo
    } : u));
    log(`Perfil actualizado: ${value.nombre} (${value.correo})`);
  };
  // Aún en localStorage (no forman parte del modelo de tablas pedido)
  const [emps, setEmps] = useStoredState<Empleado[]>('getr.empleados', E0);
  const [meds, setMeds] = useStoredState<Medicion[]>('getr.mediciones', M0);
  const [equipos, setEquipos] = useStoredState<Equipo[]>('getr.equipos', EQ0);
  const [dismissedAlerts, setDismissedAlerts] = useStoredState<string[]>('getr.alertas.resueltas', []);
  const [view, setView] = useState<View>('inicio');
  const [scopeSucId, setScopeSucId] = useState<string>('');
  const [modal, setModal] = useState<ModalKind>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Actualizar el título de la pestaña del navegador
  useEffect(() => {
    const moduleName = view === 'inicio' ? 'Panel' : MODULE_LABELS[view] || view;
    document.title = `ELECTRICIDAD | ${moduleName}`;
  }, [view]);
  // form state
  const [sN, setSN] = useState('');
  const [sD, setSD] = useState('');
  const [sT, setST] = useState(TIPOS[0]);
  const [eN, setEN] = useState('');
  const [eC, setEC] = useState('');
  const [eS, setES] = useState('');
  const [mS, setMS] = useState('');
  const [mP, setMP] = useState('');
  const [mL, setML] = useState('');
  const [mE, setME] = useState('');
  const [mN, setMN] = useState('');
  const [eqNombre, setEqNombre] = useState('');
  const [eqSerial, setEqSerial] = useState('');
  const [eqEstado, setEqEstado] = useState('Disponible');
  const [eqCal, setEqCal] = useState('');
  const [rolNombre, setRolNombre] = useState('');
  const [rolDesc, setRolDesc] = useState('');
  const [rolPermisos, setRolPermisos] = useState<Record<string, PermFlags>>(emptyPermisos());
  const [uN, setUN] = useState('');
  const [uCorreo, setUCorreo] = useState('');
  const [uPass, setUPass] = useState('');
  const [showUPass, setShowUPass] = useState(false);
  const [uRol, setURol] = useState<Usuario['rol']>('tecnico');
  const [uSucs, setUSucs] = useState<string[]>([]);
  const state: AppState = {
    sucs,
    emps,
    meds,
    perfil,
    notif,
    prefs,
    audit,
    dismissedAlerts,
    equipos,
    roles,
    usuarios,
    consumos
  };
  // ── SESSION / SCOPING ─────────────────────────────────────────────
  const currentUser = session;
  if (!currentUser) {
    return <Login onLogin={usuario => {
      setSession(usuario);
      const ids = Array.isArray(usuario.sucursalesIds) ? usuario.sucursalesIds : [];
      const admin = usuario.rol === 'admin' || ids.length === 0;
      setView(admin ? 'inicio' : 'consumo');
    }} data-id="element-0" />;
  }
  // Sesiones persistidas por versiones anteriores podían no traer
  // `sucursalesIds`; normalizamos a un arreglo para no romper el render.
  const currentSucursalesIds = Array.isArray(currentUser.sucursalesIds) ? currentUser.sucursalesIds : [];
  // OJO: estos dos conceptos son distintos y antes estaban mezclados en una
  // sola variable `isAdmin`, lo que causaba que CUALQUIER usuario sin
  // sucursales asignadas (el valor por defecto al crear uno nuevo) recibiera
  // acceso total como administrador, sin importar los permisos reales de su
  // rol. Ahora:
  // - `isAdmin`: solo es true para el rol 'admin' real. Controla el bypass
  //   de permisos (canRead/canEdit/canArchive) y el menú completo.
  // - `seesAllSucursales`: true para admin O para cualquier usuario cuyo
  //   `sucursalesIds` esté vacío (convención: [] = "todas"). Solo controla
  //   qué datos/sucursales ve, no qué puede hacer con ellos.
  const isAdmin = currentUser.rol === 'admin';
  const seesAllSucursales = isAdmin || currentSucursalesIds.length === 0;
  const scopeSucursales = seesAllSucursales ? sucs : sucs.filter(s => currentSucursalesIds.includes(s.id));
  // Siempre se ofrece el selector de sucursal en Consumo, aunque solo haya
  // una sucursal disponible (definitivo, a petición del usuario).
  const puntoFijo = false;
  const activeSuc = scopeSucursales.find(s => s.id === scopeSucId) ?? scopeSucursales[0] ?? sucs[0];
  const logout = () => {
    setSession(null);
    setView('inicio');
  };
  // ── HANDLERS (admin entity CRUD) ──────────────────────────────────
  const openNew = (target: Exclude<ModalKind, null>) => {
    setEditingId(null);
    if (target === 'sucursal') {
      setSN('');
      setSD('');
      setST(TIPOS[0]);
    }
    if (target === 'empleado') {
      setEN('');
      setEC('');
      setES('');
    }
    if (target === 'medicion') {
      setMS('');
      setMP('');
      setML('');
      setME('');
      setMN('');
    }
    if (target === 'equipo') {
      setEqNombre('');
      setEqSerial('');
      setEqEstado('Disponible');
      setEqCal('');
    }
    if (target === 'rol') {
      setRolNombre('');
      setRolDesc('');
      setRolPermisos(emptyPermisos());
    }
    if (target === 'usuario') {
      setUN('');
      setUCorreo('');
      setUPass('');
      setShowUPass(false);
      const primerRol = roles[0]?.id.replace(/^rol-/, '') as Usuario['rol'];
      setURol(primerRol ?? 'tecnico');
      setUSucs([]);
    }
    setModal(target);
  };
  const editSuc = (id: string) => {
    const s = sucs.find(x => x.id === id);
    if (!s) return;
    setEditingId(id);
    setSN(s.nombre);
    setSD(s.direccion);
    setST(s.tipo);
    setModal('sucursal');
  };
  const editRol = (id: string) => {
    const r = roles.find(x => x.id === id);
    if (!r) return;
    setEditingId(id);
    setRolNombre(r.nombre);
    setRolDesc(r.descripcion);
    setRolPermisos({
      ...emptyPermisos(),
      ...r.permisos
    });
    setModal('rol');
  };
  const editUsuario = (id: string) => {
    const u = usuarios.find(x => x.id === id);
    if (!u) return;
    setEditingId(id);
    setUN(u.nombre);
    setUCorreo(u.correo);
    // El campo queda vacío a propósito: la contraseña actual NUNCA llega al
    // cliente. Si se deja vacío al guardar, el servidor conserva el hash actual.
    setUPass('');
    setShowUPass(false);
    setURol(u.rol);
    setUSucs(u.sucursalesIds);
    setModal('usuario');
  };
  const addSuc = () => {
    if (!sN.trim()) return alert('Ingresa el nombre');
    if (editingId) {
      setSucs(sucs.map(s => s.id === editingId ? {
        ...s,
        nombre: sN,
        direccion: sD,
        tipo: sT
      } : s));
      log(`Sucursal editada: ${sN}`);
    } else {
      setSucs([...sucs, {
        id: uid(),
        nombre: sN,
        direccion: sD,
        tipo: sT,
        creado: Date.now()
      }]);
      log(`Sucursal creada: ${sN}`);
    }
    setEditingId(null);
    setModal(null);
  };
  const addEmp = () => {
    if (!eN.trim()) return alert('Ingresa el nombre');
    if (editingId) {
      setEmps(emps.map(e => e.id === editingId ? {
        ...e,
        nombre: eN,
        cargo: eC,
        sucursalId: eS
      } : e));
      log(`Empleado editado: ${eN}`);
    } else {
      setEmps([...emps, {
        id: uid(),
        nombre: eN,
        cargo: eC,
        sucursalId: eS
      }]);
      log(`Empleado creado: ${eN}`);
    }
    setEditingId(null);
    setModal(null);
  };
  const addMed = () => {
    const lux = parseFloat(mL);
    if (!mS) return alert('Selecciona sucursal');
    if (!mP.trim()) return alert('Ingresa el punto');
    if (Number.isNaN(lux) || lux < 0) return alert('Lux invalido');
    if (editingId) {
      setMeds(meds.map(m => m.id === editingId ? {
        ...m,
        sucursalId: mS,
        punto: mP,
        lux,
        empleadoId: mE,
        notas: mN
      } : m));
      log(`Medicion editada: ${mP}`);
    } else {
      setMeds([{
        id: uid(),
        sucursalId: mS,
        punto: mP,
        lux,
        empleadoId: mE,
        notas: mN,
        fecha: Date.now()
      }, ...meds]);
      log(`Medicion registrada: ${mP}`);
    }
    setEditingId(null);
    setModal(null);
  };
  const addEquipo = () => {
    if (!eqNombre.trim()) return alert('Ingresa el nombre del equipo');
    if (editingId) {
      setEquipos(equipos.map(q => q.id === editingId ? {
        ...q,
        nombre: eqNombre,
        serial: eqSerial,
        estado: eqEstado,
        calibracion: eqCal
      } : q));
      log(`Equipo editado: ${eqNombre}`);
    } else {
      setEquipos([...equipos, {
        id: uid(),
        nombre: eqNombre,
        serial: eqSerial,
        estado: eqEstado,
        calibracion: eqCal
      }]);
      log(`Equipo creado: ${eqNombre}`);
    }
    setEditingId(null);
    setModal(null);
  };
  const addRol = () => {
    if (!rolNombre.trim()) return alert('Ingresa el nombre del rol');
    if (editingId) {
      setRoles(roles.map(r => r.id === editingId ? {
        ...r,
        nombre: rolNombre,
        descripcion: rolDesc,
        permisos: rolPermisos
      } : r));
      log(`Rol editado: ${rolNombre}`);
    } else {
      setRoles([...roles, {
        id: uid(),
        nombre: rolNombre,
        descripcion: rolDesc,
        permisos: rolPermisos
      }]);
      log(`Rol creado: ${rolNombre}`);
    }
    setEditingId(null);
    setModal(null);
  };
  const togglePerm = (modulo: string, accion: keyof PermFlags) => {
    setRolPermisos(prev => {
      const cur = prev[modulo] || {
        leer: false,
        editar: false,
        archivar: false
      };
      return {
        ...prev,
        [modulo]: {
          ...cur,
          [accion]: !cur[accion]
        }
      };
    });
  };
  const toggleUSuc = (id: string) => setUSucs(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const addUsuario = async () => {
    if (!uN.trim()) return alert('Ingresa el nombre');
    if (!uCorreo.trim()) return alert('Ingresa el correo');
    if (!editingId && !uPass.trim()) {
      return alert('Ingresa una contraseña para el nuevo usuario.');
    }
    if (uPass.trim() && uPass.trim().length < 6) {
      return alert('La contraseña debe tener al menos 6 caracteres.');
    }
    const sucsIds = uRol === 'admin' ? [] : uSucs;
    const id = editingId ?? uid();
    const {
      ok,
      error
    } = await guardarUsuario({
      id,
      nombre: uN,
      correo: uCorreo,
      password: uPass.trim() || undefined,
      rol: uRol,
      sucursalesIds: sucsIds
    });
    if (!ok) {
      alert(error ?? 'No se pudo guardar el usuario.');
      return;
    }
    if (editingId) {
      setUsuarios(usuarios.map(u => u.id === editingId ? {
        ...u,
        nombre: uN,
        correo: uCorreo,
        rol: uRol,
        sucursalesIds: sucsIds
      } : u));
      log(`Usuario editado: ${uN}`);
    } else {
      setUsuarios([...usuarios, {
        id,
        nombre: uN,
        correo: uCorreo,
        rol: uRol,
        sucursalesIds: sucsIds
      }]);
      log(`Usuario creado: ${uN}`);
    }
    setEditingId(null);
    setModal(null);
  };
  const deleteSuc = (id: string) => {
    setSucs(sucs.map(s => s.id === id ? {
      ...s,
      archivado: !s.archivado
    } : s));
    log('Sucursal archivada/desarchivada');
  };
  const deleteRol = (id: string) => {
    setRoles(roles.map(x => x.id === id ? {
      ...x,
      archivado: !x.archivado
    } : x));
    log('Rol archivado/desarchivado');
  };
  const deleteUsuario = (id: string) => {
    setUsuarios(usuarios.map(x => x.id === id ? {
      ...x,
      archivado: !x.archivado
    } : x));
    log('Usuario archivado/desarchivado');
  };
  const deleteConsumo = (id: string) => {
    setConsumos(consumos.map(x => x.id === id ? {
      ...x,
      archivado: !x.archivado
    } : x));
    log('Consumo archivado/desarchivado');
  };
  const addConsumo = (sucursalId: string, fecha: number, kwh: number) => {
    setConsumos([...consumos, {
      id: uid(),
      sucursalId,
      fecha,
      kwh
    }]);
    const nombre = sucs.find(s => s.id === sucursalId)?.nombre ?? sucursalId;
    log(`Consumo registrado en ${nombre}: ${kwh} kWh`);
  };
  // ── PERMISOS DEL ROL ──────────────────────────────────────────────
  // El rol del usuario se mapea a su definición en `roles` para saber qué
  // módulos puede leer. Los roles "semilla" (admin/supervisor/tecnico)
  // guardan su id como 'rol-<rol>', pero los roles personalizados creados
  // desde el módulo "Roles y permisos" guardan su id tal cual (sin prefijo),
  // y eso es justamente lo que se guarda en `usuario.rol`. Por eso probamos
  // primero el id exacto y, si no existe, probamos con el prefijo 'rol-'.
  const userRole = roles.find(r => r.id === currentUser.rol) ?? roles.find(r => r.id === `rol-${currentUser.rol}`);
  const permisos = userRole?.permisos ?? {};
  // Solo algunos roles ven el costo estimado en Consumo (permiso 'costoConsumo').
  const canSeeCosto = isAdmin || !!permisos.costoConsumo?.leer;
  // 'inicio' (panel) y 'consumo' son la base para cualquier usuario; el resto
  // de módulos depende de los permisos de lectura del rol.
  const canRead = (id: View) => isAdmin || id === 'consumo' || id === 'inicio' || !!permisos[id]?.leer;
  const canEdit = (id: View) => isAdmin || !!permisos[id]?.editar;
  const canArchive = (id: View) => isAdmin || !!permisos[id]?.archivar;
  // ── NAV ───────────────────────────────────────────────────────────
  // Módulos visibles del producto (los demás fueron retirados de la app).
  const ALLOWED_NAV_KEYS: string[] = ['sucursales', 'reportes', 'alertas', 'gas', 'auditoria', 'configuracion'];
  const nav: {
    id: View;
    label: string;
  }[] = isAdmin ? [{
    id: 'inicio',
    label: 'Panel'
  }, {
    id: 'consumo',
    label: 'Consumo'
  }, ...ALLOWED_NAV_KEYS.map(k => ({
    id: k as View,
    label: MODULE_LABELS[k] ?? k
  }))] : [{
    id: 'inicio' as View,
    label: 'Panel'
  }, {
    id: 'consumo' as View,
    label: 'Consumo'
  }, ...ALLOWED_NAV_KEYS.filter(k => permisos[k]?.leer).map(k => ({
    id: k as View,
    label: MODULE_LABELS[k] ?? k
  }))];

  // El usuario solo ve y elige entre las sucursales que tiene asignadas
  // (admin = todas). `scopeSucursales` y `activeSuc` ya respetan ese alcance.
  const consumoScreen = activeSuc ? <ConsumoEnergetico state={state} sucursal={activeSuc} sucursalesDisponibles={scopeSucursales} puntoFijo={puntoFijo} mostrarCosto={canSeeCosto} onChangeSucursal={setScopeSucId} onSave={addConsumo} onDelete={canArchive('consumo') ? deleteConsumo : undefined} canArchive={canArchive('consumo')} data-id="element-1" /> : <div style={{
    color: T.textMuted,
    padding: 40,
    textAlign: 'center'
  }} data-id="element-2">
    
      No tienes ningún punto comercial asignado.
    </div>;

  // ── ALCANCE DE DATOS ──────────────────────────────────────────────
  // Para usuarios no-admin, todas las vistas se limitan a sus sucursales
  // asignadas. El admin (sucursalesIds vacío) ve todo.
  const viewSucs = seesAllSucursales ? sucs : scopeSucursales;
  const scopeIds = new Set(scopeSucursales.map(s => s.id));
  const viewMeds = seesAllSucursales ? meds : meds.filter(m => scopeIds.has(m.sucursalId));
  const viewEmps = seesAllSucursales ? emps : emps.filter(e => scopeIds.has(e.sucursalId));
  const viewConsumos = seesAllSucursales ? consumos : consumos.filter(c => scopeIds.has(c.sucursalId));
  const viewState: AppState = seesAllSucursales ? state : {
    ...state,
    sucs: viewSucs,
    meds: viewMeds,
    emps: viewEmps,
    consumos: viewConsumos
  };
  const noop = () => {};
  const pages: Record<string, React.ReactNode> = {
    consumo: consumoScreen,
    inicio: <Dashboard state={viewState} setView={setView} data-id="element-3" />,
    sucursales: <Sucursales sucs={viewSucs} onNew={canEdit('sucursales') ? () => openNew('sucursal') : undefined} onEdit={canEdit('sucursales') ? editSuc : undefined} onDelete={canArchive('sucursales') ? deleteSuc : undefined} canArchive={canArchive('sucursales')} data-id="element-4" />,
    gas: <GasApp data-id="element-5" />,
    reportes: <Reportes state={viewState} onLog={log} data-id="element-6" />,
    alertas: <Alertas state={viewState} resolveAlert={id => {
      setDismissedAlerts([...dismissedAlerts, id]);
      log('Alerta resuelta');
    }} data-id="element-7" />,
    auditoria: <Auditoria state={state} data-id="element-8" />,
    configuracion: <Configuracion state={state} setPerfil={setPerfil} setNotif={setNotif} setPrefs={setPrefs} usuariosNode={canRead('usuarios') ? <Usuarios state={state} onNew={canEdit('usuarios') ? () => openNew('usuario') : noop} onEdit={canEdit('usuarios') ? editUsuario : noop} onDelete={canArchive('usuarios') ? deleteUsuario : noop} canArchive={canArchive('usuarios')} data-id="element-10" /> : undefined} rolesNode={canRead('roles') ? <RolesPermisos roles={roles} onNew={canEdit('roles') ? () => openNew('rol') : noop} onEdit={canEdit('roles') ? editRol : noop} onDelete={canArchive('roles') ? deleteRol : noop} canArchive={canArchive('roles')} data-id="element-11" /> : undefined} conexionNode={isAdmin ? <ConexionSupabase data-id="element-12" /> : undefined} data-id="element-9" />
  };
  const page = canRead(view) ? pages[view] : consumoScreen;
  return <div style={{
    minHeight: '100vh',
    fontFamily: "'DM Sans',-apple-system,sans-serif",
    background: '#FFFCF8',
    color: '#1A1207'
  }} data-id="element-13">
      
      <header style={{
      ...glass,
      position: 'sticky',
      top: 0,
      zIndex: 100,
      borderRadius: 0,
      borderLeft: 'none',
      borderRight: 'none',
      borderTop: 'none',
      padding: isMobile ? '10px 14px' : '12px 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: isMobile ? 'wrap' : 'nowrap',
      gap: isMobile ? 10 : 18
    }} data-id="element-14">
        
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexShrink: 0
      }} data-id="element-15">
          
          <img src="https://cdn.magicpatterns.com/uploads/6VHLBX9XSQjy9HjvYqPJBL/pasted-image.png" alt="Junquito Gas" style={{
          height: isMobile ? 38 : 96,
          width: 'auto',
          maxWidth: isMobile ? '58vw' : 'none',
          objectFit: 'contain',
          display: 'block'
        }} data-id="element-16" />
          
        </div>
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? 8 : 12,
        flexShrink: 0,
        marginLeft: isMobile ? 'auto' : 0,
        order: isMobile ? 1 : 3
      }} data-id="element-17">
          
          <div style={{
          textAlign: 'right',
          lineHeight: 1.25
        }} data-id="element-18">
            
            <div style={{
            fontSize: 12.5,
            fontWeight: 700,
            color: '#1A1207'
          }} data-id="element-19">
              
              {currentUser.nombre}
            </div>
            <div style={{
            fontSize: 11,
            color: T.textMuted,
            textTransform: 'capitalize'
          }} data-id="element-20">
              
              {userRole?.nombre ?? currentUser.rol}
            </div>
          </div>
          <button onClick={logout} style={{
          ...ghostButton,
          padding: '7px 12px'
        }} data-id="element-21">
            
            Salir
          </button>
        </div>
        <nav style={{
        display: 'flex',
        gap: isMobile ? 6 : 4,
        flexWrap: isMobile ? 'nowrap' : 'wrap',
        justifyContent: isMobile ? 'flex-start' : 'center',
        order: isMobile ? 3 : 2,
        width: isMobile ? '100%' : 'auto',
        overflowX: isMobile ? 'auto' : 'visible',
        WebkitOverflowScrolling: 'touch',
        marginTop: isMobile ? 4 : 0,
        paddingTop: isMobile ? 8 : 0,
        paddingBottom: isMobile ? 2 : 0,
        borderTop: isMobile ? `1px solid ${T.border}` : 'none'
      }} data-id="element-22">
          
          {nav.map(item => {
          const active = view === item.id;
          return <button key={item.id} onClick={() => setView(item.id)} style={{
            padding: '7px 12px',
            borderRadius: 10,
            border: 'none',
            background: active ? 'rgba(255,107,1,.12)' : 'transparent',
            color: active ? T.accent : T.textMuted,
            fontSize: 12.5,
            fontWeight: active ? 700 : 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }} data-id="element-23">
                
                {item.label}
              </button>;
        })}
        </nav>
      </header>

      <main style={{
      padding: isMobile ? '20px 14px 48px' : '32px 24px 60px',
      maxWidth: 1160,
      margin: '0 auto'
    }} data-id="element-24">
        
        {page}
      </main>

      {modal === 'sucursal' && <Modal title={editingId ? 'Editar sucursal' : 'Nueva sucursal'} onClose={() => {
      setModal(null);
      setEditingId(null);
    }} data-id="element-25">
        
          <Field label="NOMBRE" data-id="element-26">
            <input style={inputStyle} value={sN} onChange={e => setSN(e.target.value)} placeholder="Ej: Plaza Central" data-id="element-27" />
          
          </Field>
          <Field label="DIRECCION" data-id="element-28">
            <input style={inputStyle} value={sD} onChange={e => setSD(e.target.value)} placeholder="Calle, ciudad" data-id="element-29" />
          
          </Field>
          <Field label="TIPO" data-id="element-30">
            <select style={inputStyle} value={sT} onChange={e => setST(e.target.value)} data-id="element-31">
            
              {TIPOS.map(t => <option key={t} data-id="element-32">{t}</option>)}
            </select>
          </Field>
          <button onClick={addSuc} style={{
        ...primaryButton,
        width: '100%'
      }} data-id="element-33">
          
            {editingId ? 'Guardar cambios' : 'Guardar'}
          </button>
        </Modal>}
      {modal === 'empleado' && <Modal title={editingId ? 'Editar empleado' : 'Nuevo empleado'} onClose={() => {
      setModal(null);
      setEditingId(null);
    }} data-id="element-34">
        
          <Field label="NOMBRE" data-id="element-35">
            <input style={inputStyle} value={eN} onChange={e => setEN(e.target.value)} data-id="element-36" />
          
          </Field>
          <Field label="CARGO" data-id="element-37">
            <input style={inputStyle} value={eC} onChange={e => setEC(e.target.value)} data-id="element-38" />
          
          </Field>
          <Field label="SUCURSAL" data-id="element-39">
            <select style={inputStyle} value={eS} onChange={e => setES(e.target.value)} data-id="element-40">
            
              <option value="" data-id="element-41">Sin asignar</option>
              {sucs.map(s => <option key={s.id} value={s.id} data-id="element-42">
                  {s.nombre}
                </option>)}
            </select>
          </Field>
          <button onClick={addEmp} style={{
        ...primaryButton,
        width: '100%'
      }} data-id="element-43">
          
            {editingId ? 'Guardar cambios' : 'Guardar'}
          </button>
        </Modal>}
      {modal === 'medicion' && <Modal title={editingId ? 'Editar medicion' : 'Nueva medicion'} onClose={() => {
      setModal(null);
      setEditingId(null);
    }} data-id="element-44">
        
          <Field label="SUCURSAL" data-id="element-45">
            <select style={inputStyle} value={mS} onChange={e => setMS(e.target.value)} data-id="element-46">
            
              <option value="" data-id="element-47">Seleccionar</option>
              {viewSucs.map(s => <option key={s.id} value={s.id} data-id="element-48">
                  {s.nombre}
                </option>)}
            </select>
          </Field>
          <Field label="PUNTO" data-id="element-49">
            <input style={inputStyle} value={mP} onChange={e => setMP(e.target.value)} data-id="element-50" />
          
          </Field>
          <Field label="LUX" data-id="element-51">
            <input style={inputStyle} type="number" min="0" value={mL} onChange={e => setML(e.target.value)} data-id="element-52" />
          
          </Field>
          <Field label="EMPLEADO" data-id="element-53">
            <select style={inputStyle} value={mE} onChange={e => setME(e.target.value)} data-id="element-54">
            
              <option value="" data-id="element-55">Seleccionar</option>
              {emps.map(e => <option key={e.id} value={e.id} data-id="element-56">
                  {e.nombre}
                </option>)}
            </select>
          </Field>
          <Field label="NOTAS" data-id="element-57">
            <textarea style={{
          ...inputStyle,
          resize: 'none'
        }} rows={2} value={mN} onChange={e => setMN(e.target.value)} data-id="element-58" />
          
          </Field>
          <button onClick={addMed} style={{
        ...primaryButton,
        width: '100%'
      }} data-id="element-59">
          
            {editingId ? 'Guardar cambios' : 'Registrar'}
          </button>
        </Modal>}
      {modal === 'equipo' && <Modal title={editingId ? 'Editar equipo' : 'Nuevo equipo'} onClose={() => {
      setModal(null);
      setEditingId(null);
    }} data-id="element-60">
        
          <Field label="NOMBRE" data-id="element-61">
            <input style={inputStyle} value={eqNombre} onChange={e => setEqNombre(e.target.value)} data-id="element-62" />
          
          </Field>
          <Field label="SERIAL" data-id="element-63">
            <input style={inputStyle} value={eqSerial} onChange={e => setEqSerial(e.target.value)} data-id="element-64" />
          
          </Field>
          <Field label="ESTADO" data-id="element-65">
            <select style={inputStyle} value={eqEstado} onChange={e => setEqEstado(e.target.value)} data-id="element-66">
            
              <option data-id="element-67">Disponible</option>
              <option data-id="element-68">En uso</option>
              <option data-id="element-69">Mantenimiento</option>
            </select>
          </Field>
          <Field label="PROXIMA CALIBRACION" data-id="element-70">
            <input style={inputStyle} value={eqCal} onChange={e => setEqCal(e.target.value)} data-id="element-71" />
          
          </Field>
          <button onClick={addEquipo} style={{
        ...primaryButton,
        width: '100%'
      }} data-id="element-72">
          
            {editingId ? 'Guardar cambios' : 'Guardar'}
          </button>
        </Modal>}
      {modal === 'rol' && <Modal title={editingId ? 'Editar rol' : 'Nuevo rol'} onClose={() => {
      setModal(null);
      setEditingId(null);
    }} data-id="element-73">
        
          <Field label="NOMBRE" data-id="element-74">
            <input style={inputStyle} value={rolNombre} onChange={e => setRolNombre(e.target.value)} data-id="element-75" />
          
          </Field>
          <Field label="DESCRIPCION" data-id="element-76">
            <input style={inputStyle} value={rolDesc} onChange={e => setRolDesc(e.target.value)} data-id="element-77" />
          
          </Field>
          <div style={{
        fontSize: 11,
        fontWeight: 700,
        color: '#1A1207',
        letterSpacing: '0.08em',
        marginBottom: 8
      }} data-id="element-78">
          
            PERMISOS
          </div>
          <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        maxHeight: 260,
        overflowY: 'auto',
        marginBottom: 16,
        paddingRight: 4
      }} data-id="element-79">
          
            {MODULE_KEYS.map(m => {
          const flags = rolPermisos[m] || {
            leer: false,
            editar: false,
            eliminar: false
          };
          return <div key={m} style={{
            display: 'grid',
            gridTemplateColumns: '1fr repeat(3,68px)',
            alignItems: 'center',
            gap: 8,
            padding: '8px 10px',
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: 8
          }} data-id="element-80">
                
                  <span style={{
              fontSize: 12,
              fontWeight: 700,
              color: '#1A1207',
              textTransform: 'capitalize'
            }} data-id="element-81">
                  
                    {m}
                  </span>
                  {(['leer', 'editar', 'eliminar'] as (keyof PermFlags)[]).map(a => <label key={a} style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 4,
              cursor: 'pointer',
              fontSize: 11,
              color: '#1A1207',
              textTransform: 'capitalize'
            }} data-id="element-82">
                    
                        <input type="checkbox" checked={flags[a]} onChange={() => togglePerm(m, a)} data-id="element-83" />
                    

                        {a}
                      </label>)}
                </div>;
        })}
          </div>
          <button onClick={addRol} style={{
        ...primaryButton,
        width: '100%'
      }} data-id="element-84">
          
            {editingId ? 'Guardar cambios' : 'Guardar'}
          </button>
        </Modal>}
      {modal === 'usuario' && <Modal title={editingId ? 'Editar usuario' : 'Nuevo usuario'} onClose={() => {
      setModal(null);
      setEditingId(null);
    }} data-id="element-85">
        
          <Field label="NOMBRE" data-id="element-86">
            <input style={inputStyle} value={uN} onChange={e => setUN(e.target.value)} placeholder="Nombre y apellido" data-id="element-87" />
          
          </Field>
          <Field label="CORREO" data-id="element-88">
            <input style={inputStyle} type="text" value={uCorreo} onChange={e => setUCorreo(e.target.value)} placeholder="correo@getr.do" data-id="element-89" />
          
          </Field>
          <Field label={editingId ? 'CONTRASEÑA (dejar vacío para mantener)' : 'CONTRASEÑA'} data-id="element-90">
          
            <div style={{
          position: 'relative'
        }} data-id="element-91">
            
              <input style={{
            ...inputStyle,
            paddingRight: 42
          }} type={showUPass ? 'text' : 'password'} autoComplete="new-password" value={uPass} onChange={e => setUPass(e.target.value)} placeholder="••••••" data-id="element-92" />
            

              <button type="button" onClick={() => setShowUPass(s => !s)} aria-label={showUPass ? 'Ocultar contraseña' : 'Mostrar contraseña'} style={{
            position: 'absolute',
            right: 6,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'transparent',
            border: 'none',
            color: T.textMuted,
            padding: 6,
            display: 'flex',
            cursor: 'pointer'
          }} data-id="element-93">
              
                {showUPass ? <EyeOffIcon size={17} data-id="element-94" /> : <EyeIcon size={17} data-id="element-95" />}
              </button>
            </div>
          </Field>
          <Field label="ROL" data-id="element-96">
            <select style={inputStyle} value={uRol} onChange={e => setURol(e.target.value as Usuario['rol'])} data-id="element-97">
            
              {roles.length === 0 && <option value="" disabled data-id="element-98">
                  No hay roles en la base de datos
                </option>}
              {roles.map(r => <option key={r.id} value={r.id.replace(/^rol-/, '')} data-id="element-99">
                  {r.nombre}
                </option>)}
            </select>
          </Field>
          {uRol !== 'admin' && <Field label="PUNTOS COMERCIALES ASIGNADOS" data-id="element-100">
              <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6
        }} data-id="element-101">
            
                {sucs.map(s => <label key={s.id} style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            fontSize: 13,
            color: T.textSecondary,
            padding: '6px 8px',
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: 8
          }} data-id="element-102">
              
                    <input type="checkbox" checked={uSucs.includes(s.id)} onChange={() => toggleUSuc(s.id)} data-id="element-103" />
              

                    {s.nombre}
                  </label>)}
              </div>
            </Field>}
          {uRol === 'admin' && <div style={{
        fontSize: 12,
        color: '#c4b5fd',
        marginBottom: 16
      }} data-id="element-104">
          
              El administrador tiene acceso a todos los puntos comerciales.
            </div>}
          <button onClick={addUsuario} style={{
        ...primaryButton,
        width: '100%'
      }} data-id="element-105">
          
            {editingId ? 'Guardar cambios' : 'Crear usuario'}
          </button>
        </Modal>}
    </div>;
}